import React, { useCallback, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  makeStyles,
} from '@material-ui/core'
import { isBefore, startOfDay } from 'date-fns'
import Checkbox from 'UI/Checkbox'
import RequiredOption from 'UI/Select/RequiredOption'
import convertDateForPicker from 'utils/convertDateForPicker'
import isValidDate from 'utils/isValidDate'
import { FlexReceivablesType } from 'typescript/enums/deals'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'
import { useSnackbar } from 'notistack'
import { isBeforeDate } from 'utils/dateUtils'
import { COMMISION_DUE_DATE_WARNING, FIRST_PAYMENT_DATE_WARNING } from 'constants/warnings'
import DatePickerWithTooltip from 'components/DatePickerWithTooltip'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    padding: theme.spacing(3),
    minWidth: 380,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  flexTypeTitle: {
    color: theme.palette.secondary.dark,
  },
  flexTypeContainer: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.shape.borderRadius * 3,
    backgroundColor: theme.palette.grey[50],
  },
  flexRadio: {
    padding: theme.spacing(1),
    color: theme.palette.text.primary,
    '&.Mui-checked': {
      color: theme.palette.secondary.dark,
    },
  },
  flexLabel: {
    color: theme.palette.text.primary,
  },
}))

interface Props {
  showFlexReceivablesType?: boolean
  showCustomScheduleAddendum?: boolean
  showWeeklyPaymentAddendum?: boolean
  initDealStartDate?: string
  initFirstPaymentDate?: string
  initCommissionDueDate?: string
  title?: string
  submitLabel?: string
  submit: (
    initDealStartDate: string | undefined,
    flexReceivablesType: FlexReceivablesType,
    attachCustomScheduleAddendum: boolean,
    attachWeeklyPaymentAddendum: boolean,
    achEffectiveDate: string | undefined
  ) => void
  cancel: () => void
  isLoading?: boolean
}

export const GenerateContractForm: React.FC<Props> = ({
  showFlexReceivablesType = false,
  showCustomScheduleAddendum = false,
  showWeeklyPaymentAddendum = false,
  initDealStartDate,
  initFirstPaymentDate,
  initCommissionDueDate,
  title = 'Generate DocuSign Contract',
  submitLabel = 'Generate',
  submit,
  cancel,
  isLoading,
}) => {
  const classes = useStyles()
  const snack = useSnackbar()

  const [dealStartDate, setDealStartDate] = useState<Date | null>(
    initDealStartDate ? new Date(initDealStartDate) : null
  )
  const [achEffectiveDate, setAchEffectiveDate] = useState<Date | null>(
    initDealStartDate ? new Date(initDealStartDate) : null
  )
  const [isAchEffectiveDateTouched, setIsAchEffectiveDateTouched] = useState(false)
  const [attachCustomScheduleAddendum, setAttachCustomScheduleAddendum] = useState(false)
  const [attachWeeklyPaymentAddendum, setAttachWeeklyPaymentAddendum] = useState(false)
  const [flexReceivablesType, setFlexReceivablesType] = useState<FlexReceivablesType>(FlexReceivablesType.full)

  const isPast = !!dealStartDate && isBefore(dealStartDate, startOfDay(new Date()))
  const isDateInvalid = !dealStartDate || !isValidDate(dealStartDate) || isPast
  const isAchEffectiveDateInvalid = !achEffectiveDate || !isValidDate(achEffectiveDate)

  const handleSubmit = () => {
    if (isDateInvalid || isAchEffectiveDateInvalid) {
      return
    }
    submit(
      dealStartDate ? dealStartDate.toISOString() : undefined,
      flexReceivablesType,
      attachCustomScheduleAddendum,
      showWeeklyPaymentAddendum && attachWeeklyPaymentAddendum,
      achEffectiveDate ? achEffectiveDate.toISOString() : undefined
    )
  }

  const handleAchEffectiveDateChange = useCallback((value: MaterialUiPickersDate) => {
    setIsAchEffectiveDateTouched(true)
    setAchEffectiveDate(convertDateForPicker(value) as Date)
  }, [])

  const handleDealStartDateChange = useCallback((value: MaterialUiPickersDate) => {
     const date = convertDateForPicker(value) as Date;

     setDealStartDate(date)

     if (!isAchEffectiveDateTouched) {
       setAchEffectiveDate(date)
     }

     if (initFirstPaymentDate && isBeforeDate(initFirstPaymentDate, date)) {
        snack.enqueueSnackbar(FIRST_PAYMENT_DATE_WARNING, 
        { 
          variant: 'warning',
          autoHideDuration: 10000
        })
     }

     if (initCommissionDueDate && isBeforeDate(initCommissionDueDate, date)) {
        snack.enqueueSnackbar(COMMISION_DUE_DATE_WARNING, 
        { 
          variant: 'warning',
          autoHideDuration: 10000
        })
     }
    }, [initFirstPaymentDate, initCommissionDueDate, isAchEffectiveDateTouched])

  return (
    <div className={classes.root}>
      <Typography variant="h3" color="textSecondary">
        {title}
      </Typography>
      <Box mt={2} maxWidth={260}>
        <DatePickerWithTooltip
          fullWidth
          type="keyboard"
          disablePast
          label={<RequiredOption label="Deal Start Date" />}
          InputLabelProps={{ shrink: true }}
          name="firstPaymentDate"
          value={dealStartDate}
          onChange={handleDealStartDateChange}
          error={!isValidDate(dealStartDate) || isPast}
          helperText={
            isPast
              ? 'The deal start date cannot be in the past. Please select a valid current or future date to proceed with approval.'
              : !isValidDate(dealStartDate)
              ? 'MM/DD/YYYY'
              : undefined
          }
          tooltipText="The date when the deal officially starts, the deal terms become active and the first disbursement is made."
        />
      </Box>
      <Box mt={2} maxWidth={260}>
        <DatePickerWithTooltip
          fullWidth
          type="keyboard"
          label={<RequiredOption label="Effective ACH Authorization Date" />}
          InputLabelProps={{ shrink: true }}
          name="achEffectiveDate"
          value={achEffectiveDate}
          onChange={handleAchEffectiveDateChange}
          error={isAchEffectiveDateInvalid}
          helperText={isAchEffectiveDateInvalid ? 'MM/DD/YYYY' : undefined}
          tooltipText="The date from which the ACH Authorization Form becomes effective and authorizes ACH payments for the deal."
        />
      </Box>
      {showWeeklyPaymentAddendum && (
        <FormControlLabel
          control={
            <Checkbox
              name="attachWeeklyPaymentAddendum"
              onChange={(e) => setAttachWeeklyPaymentAddendum(e.target.checked)}
              checked={attachWeeklyPaymentAddendum}
              color="primary"
            />
          }
          label={
            <Typography color="textSecondary">
              Add the Weekly Payment Addendum to the contract
            </Typography>
          }
        />
      )}
      {showFlexReceivablesType && (
        <Box>
          <Typography className={classes.flexTypeTitle}>Select Flex Addendum type:</Typography>
          <RadioGroup
            className={classes.flexTypeContainer}
            value={flexReceivablesType}
            onChange={(e) => setFlexReceivablesType(e.target.value as FlexReceivablesType)}
          >
            <FormControlLabel
              value={FlexReceivablesType.full}
              control={<Radio color="default" size="small" className={classes.flexRadio} />}
              label={<Typography className={classes.flexLabel}>Full Receivables Purchased</Typography>}
            />
            <FormControlLabel
              value={FlexReceivablesType.partial}
              control={<Radio color="default" size="small" className={classes.flexRadio} />}
              label={<Typography className={classes.flexLabel}>Partial Receivables Purchased</Typography>}
            />
          </RadioGroup>
        </Box>
      )}
      {showCustomScheduleAddendum && (
        <FormControlLabel
          control={
            <Checkbox
              name="attachCustomScheduleAddendum"
              onChange={(e) => setAttachCustomScheduleAddendum(e.target.checked)}
              checked={attachCustomScheduleAddendum}
              color="primary"
            />
          }
          label={
            <Typography color="textSecondary">Attach Schedule Addendum to contract</Typography>
          }
        />
      )}
      <div className={classes.actions}>
        <Button onClick={cancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isDateInvalid || isAchEffectiveDateInvalid || isLoading}
        >
          {isLoading ? <CircularProgress size={20} /> : submitLabel}
        </Button>
      </div>
    </div>
  )
}
